import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '../../lib/utils';

const AnimatedSlider = ({ slides, autoPlay = true, interval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
    setProgress(0); // Reset progress when manually selecting a slide
  }, []);

  const goToPrev = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? slides.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  };

  const goToNext = () => {
    const isLastSlide = currentIndex === slides.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    goToSlide(newIndex);
  };

  // Auto-play functionality with progress tracking
  useEffect(() => {
    if (!autoPlay) return;

    let intervalId;
    let progressInterval;

    if (!isHovered) {
      intervalId = setTimeout(() => {
        goToNext();
      }, interval);

      // Update progress bar gradually
      const startTime = Date.now();
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const currentProgress = Math.min((elapsed / interval) * 100, 100);
        setProgress(currentProgress);

        if (currentProgress < 100) {
          progressInterval = requestAnimationFrame(updateProgress);
        }
      };
      progressInterval = requestAnimationFrame(updateProgress);
    }

    return () => {
      if (intervalId) clearTimeout(intervalId);
      if (progressInterval) cancelAnimationFrame(progressInterval);
    };
  }, [currentIndex, autoPlay, isHovered, goToNext, interval]);

  // Initialize progress when component mounts
  useEffect(() => {
    setProgress(0);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        goToPrev();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToPrev, goToNext]);

  return (
    <div 
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setProgress(0); // Reset progress on mouse leave to restart the timer
      }}
    >
      {/* Slides container with transition */}
      <div className="relative h-[600px] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={cn(
              'absolute inset-0 transition-all duration-1000 ease-in-out flex items-center',
              index === currentIndex 
                ? 'opacity-100 z-10' 
                : 'opacity-0 z-0 scale-105',
              index === (currentIndex - 1 + slides.length) % slides.length
                ? '-translate-x-full opacity-0 scale-105 z-0'
                : index === (currentIndex + 1) % slides.length
                ? 'translate-x-full opacity-0 scale-105 z-0'
                : ''
            )}
          >
            <div className="relative w-full h-full">
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 z-10"></div>
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700"
              />
              
              <div className="absolute inset-0 z-20 flex items-center">
                <div className="container mx-auto px-8">
                  <div className="max-w-2xl ml-auto text-right text-white">
                    <div className="inline-block bg-[#D91656]/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                      <span className="text-sm font-medium">{slide.category}</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold mb-4 transform transition-all duration-700 translate-y-4 opacity-0 animate-fade-in-up">
                      {slide.title}
                    </h2>
                    <p className="text-lg md:text-xl mb-8 transform transition-all duration-700 delay-150 translate-y-4 opacity-0 animate-fade-in-up">
                      {slide.description}
                    </p>
                    {slide.buttonText && (
                      <Button 
                        className="transform transition-all duration-700 delay-300 translate-y-4 opacity-0 animate-fade-in-up bg-[#D91656] hover:bg-[#B01348] text-white"
                        onClick={slide.onClick}
                      >
                        {slide.buttonText}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation buttons - always visible with reduced opacity */}
      <Button
        variant="outline"
        size="icon"
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-3 opacity-70 hover:opacity-100 transition-all duration-300 z-30"
        onClick={goToPrev}
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-3 opacity-70 hover:opacity-100 transition-all duration-300 z-30"
        onClick={goToNext}
      >
        <ArrowRight className="h-5 w-5" />
      </Button>

      {/* Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={cn(
              'w-3 h-3 rounded-full transition-all duration-300',
              index === currentIndex 
                ? 'bg-white w-6' 
                : 'bg-white/50 hover:bg-white/80'
            )}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-black/20 z-30">
        <div 
          className="h-full bg-white"
          style={{
            width: `${progress}%`,
          }}
        ></div>
      </div>
    </div>
  );
};

export default AnimatedSlider;